import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { StatsCardData } from '../services/dashboardApi';

interface AnimatedStatsCardProps {
  data: StatsCardData;
}

const AnimatedStatsCard: React.FC<AnimatedStatsCardProps> = ({ data }) => {
  const [count, setCount] = useState(0);
  const navigate = useNavigate();
  const duration = 1500; // Animation duration in ms

  useEffect(() => {
    let startTime: number | null = null;
    const startValue = 0;
    const endValue = data.value;

    const animate = (currentTime: number) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);
      
      // Easing function for smooth animation
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      const currentCount = Math.floor(startValue + (endValue - startValue) * easeOutQuart);
      
      setCount(currentCount);

      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };

    requestAnimationFrame(animate);
  }, [data.value]);

  // Handle card click to navigate to MyTasks with appropriate filters
  const handleCardClick = () => {
    let queryParams = '';
    
    switch (data.title) {
      case 'Completed Tasks Today':
        queryParams = '?status=COMPLETED';
        break;
      case 'New Tasks':
        queryParams = '?status=NEW';
        break;
      case 'Urgent Tasks':
        queryParams = '?priority=URGENT';
        break;
      case 'Total Active Tasks':
        queryParams = '?status=IN_PROGRESS';
        break;
      default:
        queryParams = '';
    }

    navigate(`/my-tasks/my-tasks-listing${queryParams}`);
  };

  return (
    <div 
      className="card card-flush h-100 shadow-sm hover-elevate-up transition-all"
      onClick={handleCardClick}
      style={{ cursor: 'pointer' }}
    >
      <div 
        className="card-body d-flex flex-column justify-content-between p-6"
        style={{
          background: `linear-gradient(135deg, ${data.color}15 0%, ${data.color}05 100%)`,
          borderLeft: `4px solid ${data.color}`,
        }}
      >
        <div className="mb-3">
          <h3 className="text-gray-800 fw-bold fs-6 mb-0">{data.title}</h3>
          {data.subtitle && (
            <span className="text-gray-600 fs-7 fw-semibold">{data.subtitle}</span>
          )}
        </div>
        <div className="d-flex align-items-end">
          <span 
            className="fw-bolder fs-2x"
            style={{ color: data.color }}
          >
            {count}
          </span>
        </div>
      </div>
    </div>
  );
};

export default AnimatedStatsCard;
